import { useState, useCallback } from "react";
import { useDebouncedCallback } from "use-debounce";
import useInfiniteScroll from "react-infinite-scroll-hook";

/**
 * Returns common properties and functions for a data source pagination
 *
 * @param {function} dataService to execute
 */
const INITIALSTATE = {
  id: null,
  keyword: null,
};
const INITIALREQUESTSTATE = {
  message: "",
  type: 0,
  isFetching: false,
  isError: false,
  done: false,
  severity: "",
};
export const useBrowserFile = (
  dataService,
  createFolderService,
  createFileService,
  deleteFolder,
  deleteFile,
  size,
  paramaterId
) => {
  const [order, setOrder] = useState("");
  const [orderBy, setOrderBy] = useState("");
  const [filter, setFilter] = useState(INITIALSTATE);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [sourceData, setSourceData] = useState([]);
  const [totalDataRecords, setTotalDataRecords] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(size);
  const [currentTotalPages, setCurrentTotalPages] = useState(0);
  const [error, setError] = useState({ isError: false, message: "" });
  const [request, setRequest] = useState(INITIALREQUESTSTATE); // type=1:getrecords,type=2:create folder,type=3:create File

  const handleResetData = () => {
    setSourceData([]);
    setTotalDataRecords(0);
    setCurrentPageNumber(1);
    setCurrentTotalPages(0);
    setHasNextPage(true);
  };
  const handleHomeDirectory = () => {
    setFilter(INITIALSTATE);
    handleResetData();
  };

  const handleEndOfLine = () => {
    setHasNextPage(false);
    setCurrentPageNumber(1);
    setCurrentTotalPages(0);
  };

  const handleDeleteFile = async ({ parentId }) => {
    setRequest(INITIALREQUESTSTATE);
    try {
      setIsFetching(true);
      await deleteFile({ paramaterId, parentId });
      setRequest({
        message: "The file was deleted",
        type: 3,
        isFetching: false,
        done: true,
        severity: "success",
      });
      handleResetData();
    } catch (e) {
      let message = "default message";
      if (e.response) if (e.response.data) message = e.response.data.errors;
      handleEndOfLine();
      setError({ isError: true, message });
      setRequest({
        message,
        type: 3,
        isFetching: false,
        done: true,
        isError: true,
        severity: "error",
      });
    } finally {
      setIsFetching(false);
    }
  };
  const handleDeleteFolder = async ({ parentId }) => {
    setRequest(INITIALREQUESTSTATE);
    try {
      setIsFetching(true);
      await deleteFolder({ paramaterId, parentId });
      setRequest({
        message: "The folder was deleted",
        type: 2,
        isFetching: false,
        done: true,
        severity: "success",
      });
      handleResetData();
    } catch (e) {
      let message = "default message";
      if (e.response) if (e.response.data) message = e.response.data.errors;
      handleEndOfLine();
      setError({ isError: true, message });
      setRequest({
        message,
        type: 2,
        isFetching: false,
        done: true,
        isError: true,
        severity: "error",
      });
    } finally {
      setIsFetching(false);
    }
  };
  const handleCreateFile = async (file, parentId, isPrivate) => {
    setRequest(INITIALREQUESTSTATE);
    try {
      setIsFetching(true);
      const formData = new FormData();
      if (parentId !== undefined && parentId !== null)
        formData.append("parentId", parentId);

      formData.append("isPrivate", isPrivate);
      formData.append("file", file);
      await createFileService({ paramaterId, formData });
      setRequest({
        message: "The file was created",
        type: 3,
        isFetching: false,
        done: true,
        severity: "success",
      });
      handleResetData();
    } catch (e) {
      let message = "default message";
      if (e.response) if (e.response.data) message = e.response.data.errors;
      handleEndOfLine();
      setError({ isError: true, message });
      setRequest({
        message,
        type: 3,
        isFetching: false,
        done: true,
        isError: true,
        severity: "error",
      });
    } finally {
      setIsFetching(false);
    }
  };
  const handleCreateFolder = async ({ isPrivate, name, parentId }) => {
    setRequest(INITIALREQUESTSTATE);
    try {
      setIsFetching(true);
      await createFolderService({
        paramaterId,
        isPrivate,
        name,
        parentId,
      });
      setRequest({
        message: "The folder was created",
        type: 2,
        isFetching: false,
        done: true,
        severity: "success",
      });
      handleResetData();
    } catch (e) {
      let message = "default message";
      if (e.response) if (e.response.data) message = e.response.data.errors;
      handleEndOfLine();
      setError({ isError: true, message });
      setRequest({
        message,
        type: 2,
        isFetching: false,
        done: true,
        isError: true,
        severity: "error",
      });
    } finally {
      setIsFetching(false);
    }
  };
  const debouncedHandleSortChange = useDebouncedCallback((field, orderData) => {
    setOrderBy(field);
    setOrder(orderData);
    handleResetData();
  }, 1000);

  const handleLoadData = useCallback(async () => {
    try {
      setIsFetching(true);
      let queryParams = `PageNumber=${currentPageNumber}&PageSize=${currentPageSize}`;
      if (filter.keyword !== null) queryParams += `&KeyWord=${filter.keyword}`;
      if (filter.id !== null) queryParams += `&parentId=${filter.id}`;
      if (orderBy !== "" && order !== "")
        queryParams += `&OrderBy=${orderBy}&SortOrder=${order}`;
      const response = await dataService({ queryParams });

      if (response.status === 200) {
        const {
          data: { totalRecords, data },
        } = response.data;

        if (data.length > 0) {
          setHasNextPage(true);
          setSourceData([...sourceData, ...data]);
          setCurrentPageNumber(currentPageNumber + 1);
          setCurrentTotalPages(Math.floor(totalRecords / size));
        } else {
          handleEndOfLine();
        }

        setTotalDataRecords(totalRecords);
      }
    } catch (e) {
      let message = "default message";
      if (e.response) if (e.response.data) message = e.response.data.errors;
      setRequest({ ...request, message, isError: true });
      handleEndOfLine();
    } finally {
      setIsFetching(false);
    }
  }, [
    dataService,
    currentPageNumber,
    currentPageSize,
    order,
    orderBy,
    filter.id,
    filter.keyword,
    sourceData,
    size,
    request,
  ]);

  const removeItemFromData = useCallback(
    (itemId = null) => {
      setSourceData((prevSourceData) => {
        const newSourceData = prevSourceData.filter(
          (data) => data.participantProfileId !== itemId
        );
        const shouldUpdateTotalDataRecords =
          newSourceData.length !== prevSourceData.length;
        if (shouldUpdateTotalDataRecords) {
          setTotalDataRecords(totalDataRecords - 1);
        }

        return newSourceData;
      });
    },
    [totalDataRecords]
  );

  const debouncedHandleFilterChange = useDebouncedCallback((value, name) => {
    setFilter({ ...filter, [name]: value });
    handleResetData();
  }, 1000);

  const infiniteRef = useInfiniteScroll({
    loading: isFetching,
    hasNextPage,
    onLoadMore: handleLoadData,
  });

  return {
    debouncedHandleFilterChange: debouncedHandleFilterChange.callback,
    handleCreateFolder,
    handleResetData,
    setCurrentPageSize,
    setError,
    removeItemFromData,
    handleCreateFile,
    handleDeleteFolder,
    handleDeleteFile,
    infiniteRef,
    isFetching,
    sourceData,
    totalDataRecords,
    currentPageSize,
    currentTotalPages,
    error,
    handleHomeDirectory,
    request,
    setRequest,
    debouncedHandleSortChange: debouncedHandleSortChange.callback,
    order,
    orderBy,
  };
};
